import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import React, { ButtonHTMLAttributes, ReactElement, useState } from 'react';
import LoadingItemSpinner from '../../../pages/ProjectPage/ProjectAppBar/StatusIndicator/LoadingItem/LoadingItemSpinner';
import { VoxelTooltipShortProps } from '../Tooltip/VoxelTooltipShort';
import VoxelTooltipShort from '../Tooltip/VoxelTooltipShort';
import VoxelText from '../Text/VoxelText';
import { VoxelAlertSeverity } from '../Alert/VoxelAlert';

export interface VoxelButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  title: string | React.ReactNode;
  startIcon?: ReactElement<FontAwesomeIconProps>;
  endIcon?: ReactElement<FontAwesomeIconProps>;
  color?: VoxelButtonColor;
  size?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  iconSpacing?: 'fa' | 'mui';
  shape?: 'round' | 'square' | 'custom';
  textColorClass?: string;
  hoverTextColorClass?: string;
  roundedClass?: string;
  tooltip?: string;
  className?: string;
  whiteSpaceClass?: string;
  fullWidth?: boolean;
  tooltipProps?: Partial<VoxelTooltipShortProps>;
  submitting?: boolean;
  active?: boolean;
  border?: boolean;
}

export type VoxelButtonColor =
  | 'primary'
  | 'white'
  | 'transparent'
  | 'indigo'
  | 'violet'
  | 'sky'
  | 'light-gray'
  | 'gray'
  | 'blue'
  | VoxelAlertSeverity;

const VoxelButton = React.forwardRef<HTMLButtonElement, VoxelButtonProps>(
  (
    {
      onClick,
      onMouseDown,
      onMouseUp,
      title,
      disabled,
      startIcon,
      endIcon,
      color = 'light-gray',
      size = 'lg',
      iconSpacing = 'fa',
      shape = 'square',
      textColorClass,
      hoverTextColorClass,
      roundedClass,
      tooltip,
      className,
      whiteSpaceClass = 'whitespace-nowrap',
      fullWidth = false,
      tooltipProps,
      submitting,
      active,
      border,
      ...otherProps
    },
    ref
  ) => {
    const [loading, setLoading] = useState(false);

    // ---- COLOR ----
    let textColor = '';
    let hoverTextColor = '';
    let hoverBgColor = '';
    let bgColor = '';
    let activeBgColor = '';
    let shadow = '';
    let hoverShadow = '';
    let borderClasses = '';

    if (active && color === 'gray') color = 'blue';
    if (active && color === 'light-gray') color = 'gray';
    if (active && color === 'white') color = 'light-gray';

    if (color === 'primary') {
      bgColor = 'bg-primary-600';
      hoverBgColor = 'hover:bg-primary-600/90';
      activeBgColor = 'active:bg-primary-600';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'transparent') {
      bgColor = 'bg-transparent';
      hoverBgColor = 'hover:opacity-90';
      activeBgColor = 'active:opacity-80';
      textColor = textColorClass ? textColorClass : 'text-voxel-gray-600';
      hoverTextColor = hoverTextColorClass ? hoverTextColorClass : 'hover:text-voxel-gray-400';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-none';
    }
    if (color === 'white') {
      bgColor = 'bg-white';
      hoverBgColor = 'hover:bg-voxel-gray-50';
      activeBgColor = 'active:bg-voxel-gray-50';
      textColor = textColorClass ? textColorClass : 'text-voxel-gray-600';
      hoverTextColor = hoverTextColorClass ? hoverTextColorClass : 'hover:text-voxel-gray-400';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-none';
      borderClasses = border ? 'border border-voxel-gray-600' : borderClasses;
    }
    if (color === 'light-gray') {
      bgColor = 'bg-voxel-gray-200/60';
      hoverBgColor = 'hover:bg-voxel-gray-200/90';
      activeBgColor = 'active:bg-voxel-gray-200/60';
      textColor = textColorClass ? textColorClass : 'text-voxel-gray-700';
      hoverTextColor = hoverTextColorClass ? hoverTextColorClass : 'hover:text-voxel-gray-600';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'gray') {
      bgColor = 'bg-voxel-gray-300/60';
      hoverBgColor = 'hover:bg-voxel-gray-300/80';
      activeBgColor = 'active:bg-voxel-gray-300/60';
      textColor = textColorClass ? textColorClass : 'text-voxel-gray-700';
      hoverTextColor = hoverTextColorClass ? hoverTextColorClass : 'hover:text-voxel-gray-600';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'indigo') {
      bgColor = 'bg-indigo-500/90';
      hoverBgColor = 'hover:bg-indigo-500';
      activeBgColor = 'active:bg-indigo-500/90';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'violet') {
      bgColor = 'bg-violet-600/80';
      hoverBgColor = 'hover:bg-violet-600/90';
      activeBgColor = 'active:bg-violet-600/80';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'sky') {
      bgColor = 'bg-sky-500/80';
      hoverBgColor = 'hover:bg-sky-500/90';
      activeBgColor = 'active:bg-sky-500/80';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'success') {
      bgColor = 'bg-success-600/60';
      hoverBgColor = 'hover:bg-success-600/70';
      activeBgColor = 'active:bg-success-600/60';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'warning') {
      bgColor = 'bg-warning-600/60';
      hoverBgColor = 'hover:bg-warning-600/70';
      activeBgColor = 'active:bg-warning-600/60';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'error') {
      bgColor = 'bg-error-500/90';
      hoverBgColor = 'hover:bg-error-500';
      activeBgColor = 'active:bg-error-500/90';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'info') {
      bgColor = 'bg-info-600/80';
      hoverBgColor = 'hover:bg-info-600/70';
      activeBgColor = 'active:bg-info-600/80';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (color === 'blue') {
      bgColor = 'bg-slate-600';
      hoverBgColor = 'hover:bg-slate-600/90';
      activeBgColor = 'active:bg-slate-600';
      textColor = 'text-white';
      shadow = 'shadow-none';
      hoverShadow = 'hover:shadow-sm';
    }
    if (disabled) {
      bgColor = 'bg-slate-100';
      hoverBgColor = '';
      activeBgColor = '';
      textColor = 'text-slate-400';
      hoverTextColor = '';
    }
    if (active) {
      hoverBgColor = '';
      hoverTextColor = '';
    }

    // ---- SIZE ----
    let padding = '';
    let fontSize = '';


    if (size === '2xs') {
      padding = `py-[3px] px-2
     ${iconSpacing === 'mui' ? 'gap-1' : 'gap-0.5'}
      ${endIcon && 'pl-2'}
      ${startIcon && 'pr-2'}
    `;
      fontSize = 'text-[11px]';
    }
    if (size === 'xs') {
      padding = `py-1 px-2
     ${iconSpacing === 'mui' ? 'gap-1' : 'gap-1.5'}
      ${endIcon && 'pl-2.5'}
      ${startIcon && 'pr-2.5'}
    `;
      fontSize = 'text-xs';
    }
    if (size === 'sm') {
      padding = `
      py-[5px] px-3
      ${iconSpacing === 'mui' ? 'gap-1' : 'gap-1.5'}
      ${endIcon && 'pl-3.5'}
      ${startIcon && 'pr-3.5'}
    `;
      fontSize = 'text-xs';
    }
    if (size === 'md') {
      padding = `
    ${iconSpacing === 'mui' ? 'gap-1' : 'gap-1.5'}
      py-1.5 px-3
      ${endIcon && 'pl-3.5'}
      ${startIcon && 'pr-3.5'}
    `;
      fontSize = 'text-[13px]';
    }
    if (size === 'lg') {
      padding = `
    ${iconSpacing === 'mui' ? 'gap-1.5' : 'gap-2'}
      py-[7.5px] px-3
      ${endIcon && 'pl-3.5'}
      ${startIcon && 'pr-3.5'}
    `;
      fontSize = 'text-sm';
    }
    if (size === 'xl') {
      padding = `
      ${iconSpacing === 'mui' ? 'gap-2' : 'gap-2.5'}
      py-2.5 px-3.5
      ${endIcon && 'pl-4'}
      ${startIcon && 'pr-4'}
    `;
      fontSize = 'text-sm';
    }

    const startIconLoading =
      (submitting || loading) && !!startIcon ? <LoadingItemSpinner sizeClass="h-3 w-3" /> : null;
    const startIconFinal = startIconLoading ?? startIcon;

    const endIconLoading =
      (submitting || loading) && !startIcon ? <LoadingItemSpinner sizeClass="h-3 w-3" /> : null;
    const endIconFinal = endIconLoading ?? endIcon;

    return (
      <VoxelTooltipShort closeOnClick message={tooltip} showOnFocus={false}>
        <button
          ref={ref}
          onClick={(event) => {
            if (!onClick) return;
            const possiblePromise: Promise<unknown> = onClick(event) as any;
            if (!!possiblePromise?.finally) {
              setLoading(true);
              possiblePromise.finally(() => {
                setLoading(false);
              });
            }
          }}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          type={otherProps.type ?? 'button'}
          disabled={disabled || loading}
          className={`
                ${className}
                ${fullWidth ? 'w-full' : 'w-fit'}
                h-fit
                ${!disabled && 'cursor-pointer'}
                select-none
                transition duration-50
                inline-block
                ${shape === 'round' && 'rounded-full'}
                ${shape === 'square' && size !== '2xs' ? 'rounded-md' : 'rounded'}
                ${shape === 'custom' && roundedClass}
                ${padding}
                ${bgColor} ${!disabled && hoverBgColor} ${!disabled && activeBgColor}
                ${shadow} ${!disabled && hoverShadow}
                ${whiteSpaceClass}
                ${borderClasses}
                font-sans ${fontSize} font-semibold leading-normal
                ${textColor} ${!disabled && hoverTextColor}
                flex items-center justify-center
            `}
          {...otherProps}
        >
          {startIconFinal && <div>{startIconFinal}</div>}
          <div className="flex-grow">
            <VoxelText variant="bread-semibold" align="center">
              {title}
            </VoxelText>
          </div>
          {endIconFinal && <div>{endIconFinal}</div>}
        </button>
      </VoxelTooltipShort>
    );
  }
);

export default VoxelButton;
